import { useMemo } from 'react'
import { useAppSelector } from 'hooks'
import { usePermissions } from 'hooks/usePermissions'
import { useManagedTeamGroups } from 'hooks/useManagedTeamGroups'
import { dispatchManagerDefaultMenuIds } from 'layouts/components/sidebar/menuPresets'
import { isDispatchManagerRunning } from 'utils/dispatchManager'

/**
 * Which menu selection actually applies to the person looking at the sidebar.
 *
 * Two sources, and they cannot both win: a role's selection (`usePermissions`, from the custom role or the
 * system role template) and the Dispatch Manager's, which is not a role's at all.
 *
 * A Dispatch Manager is a DISPATCHER who runs at least one desk (ADR-027) — derived from group membership,
 * never stored as a `UserRole`, deliberately (see `DISPATCH_MANAGER_ROLE_VALUE`). Without this they match
 * the Dispatcher template and inherit a plain dispatcher's menus, which is the wrong half of their job:
 * they work the board AND the people on it. So their selection comes from the application, and when nobody
 * has chosen one it defaults to both presets rather than to "no restriction".
 *
 * One hook so the two can't disagree, and so the owner exemption is stated once.
 *
 * MEMOIZED, and not as a nicety: the Sidebar takes this as a dependency of the `flags` memo that feeds
 * `resolveSidebarMenu`. A fresh array each render would recompute the whole filter pipeline — groups,
 * active paths, layout choice — on every render, for exactly the users this hook exists to serve, since
 * the default branch is the one everybody is on until an owner configures them.
 */
export const useSidebarMenuSelection = (): string[] | undefined => {
    const { visibleMenuIds, isOwner, isAdmin } = usePermissions()
    const user = useAppSelector((state) => state.auth.user)
    const dispatchManagerMenuIds = useAppSelector((state) => state.auth.application?.rbac?.dispatchManagerMenuIds)

    // Admins and the owner are never restricted, so they never need the request. Everyone else pays one
    // cached call — `useManagedTeamGroups` shares it with `useBoxTruckAccess`, which the sidebar already calls.
    const { groups, loaded } = useManagedTeamGroups(!isOwner && !isAdmin)

    const runsADesk = groups.length > 0
    // Narrowed to the two fields the predicate reads, so the memo does not re-run on every unrelated change
    // to the user object — `/auth/verify` replaces it wholesale.
    const userRole = user?.role
    const rbacRoleId = user?.rbacRoleId

    return useMemo(() => {
        // The owner can always see everything: the selection is undone from Settings, and an owner who hid
        // Settings from themselves would have hidden the way back out.
        if (isOwner) return undefined

        // The shared predicate, not a copy of it: the role picker asks the same question from the other end
        // (full groups plus `managers[]`), and two hand-kept versions could disagree about the same person.
        // A custom role wins there and here — it is the role an admin deliberately built.
        const isDispatchManager = loaded && isDispatchManagerRunning({ role: userRole, rbacRoleId }, runsADesk)

        // Not a manager includes the window before `loaded`: the role's answer is already correct for a plain
        // dispatcher, so a manager sees it for a moment rather than seeing an unrestricted rail flash.
        if (!isDispatchManager) return visibleMenuIds

        return dispatchManagerMenuIds ?? dispatchManagerDefaultMenuIds()
    }, [isOwner, loaded, rbacRoleId, userRole, runsADesk, visibleMenuIds, dispatchManagerMenuIds])
}
